import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './CollegeAnalytics.module.css';
import { collegeData } from '../utils/collegeData';
import { getHeaderImage } from '../utils/collegeImageMapper';
import MultiMetricRadarChart from './analytics/MultiMetricRadarChart';
import DeadlineTimeline from './analytics/DeadlineTimeline';
import CircularProgressGauge from './CircularProgressGauge';
import ProfilePanel from './ProfilePanel';
import { API_BASE_URL } from '../utils/apiConfig';

const CollegeAnalytics = () => {
  const location = useLocation();
  const { college } = location.state || {};
  const { collegeName } = useParams();
  
  // Find college data first - before any hooks that might use it
  const foundCollege = college || collegeData.find(c => c.name === collegeName);
  
  const [analysis, setAnalysis] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [breakdownModal, setBreakdownModal] = useState({ open: false, stat: null, content: '', suggestions: null });
  const [chanceBreakdownModal, setChanceBreakdownModal] = useState({ open: false, content: '' });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [userProfile, setUserProfile] = useState(() => {
    // Initialize user profile immediately - use academicProfile key to match ProfilePanel
    return JSON.parse(localStorage.getItem('academicProfile')) || {};
  });

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
    hover: { scale: 1.03, transition: { duration: 0.2, type: "spring" } }
  };

  const listVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: { opacity: 1, x: 0 }
  };

  // Add a mapping of college-specific tips (from Analytics_backup.js)
  const collegeTips = {
    'Stanford University': {
      GPA: [
        'Focus on excelling in your remaining courses to strengthen your academic profile.',
        'Show leadership in extracurriculars to offset a lower GPA.',
        'Craft compelling essays that show genuine fit and passion.'
      ],
      SAT: [
        'Aim for a score above 1535 to be at or above the median.',
        'Take full-length practice tests to identify weak areas.',
        'Consider SAT subject tests if available and relevant.'
      ],
      ACT: [
        'Target a score of 34 or higher for a competitive application.',
        'Focus on your weakest ACT section for the biggest gains.',
        'Practice with official ACT materials for best results.'
      ]
    },
    'MIT': {
      GPA: [
        'Excel in advanced math and science courses.',
        'Engage in hands-on STEM projects or competitions.',
        'Show teamwork and leadership in group settings.'
      ],
      SAT: [
        'Aim for a score above 1550 for MIT competitiveness.',
        'Focus on math and evidence-based reading/writing equally.',
        'Use official SAT practice resources for targeted prep.'
      ],
      ACT: [
        'A score of 35+ is typical for MIT admits.',
        'Practice science and math sections for top scores.',
        'Highlight problem-solving and innovation in your application.'
      ]
    },
    // Add more colleges as needed...
  };

  // Helper functions for dynamic metric analysis
  const getPositionDescription = (userScore, p25, p75, metricType) => {
    if (!userScore || !p25 || !p75) return "insufficient data";
    if (userScore >= p75) return "exceptional strength";
    if (userScore >= p25) return "competitive position";
    return "area needing improvement";
  };

  const getGPAContext = (gpa) => {
    if (gpa >= 3.8) return "demonstrates consistent academic excellence";
    if (gpa >= 3.5) return "shows solid academic performance";
    if (gpa >= 3.0) return "indicates average academic achievement";
    return "suggests academic challenges that need addressing";
  };

  const getTestContext = (score, testType) => {
    if (testType === 'SAT') {
      if (score >= 1500) return "demonstrates exceptional reasoning and problem-solving skills";
      if (score >= 1400) return "shows strong academic readiness for college-level work";
      if (score >= 1300) return "indicates solid preparation for college academics";
      if (score >= 1200) return "suggests adequate preparation with room for improvement";
      return "indicates areas that need focused development";
    } else if (testType === 'ACT') {
      if (score >= 34) return "demonstrates exceptional academic readiness";
      if (score >= 30) return "shows strong preparation for college-level work";
      if (score >= 26) return "indicates solid academic foundation";
      if (score >= 22) return "suggests adequate preparation with growth potential";
      return "indicates areas requiring focused improvement";
    }
    return "shows your current academic standing";
  };

  const getCollegeSelectivityContext = (acceptanceRate) => {
    if (acceptanceRate <= 10) return "extremely competitive environment where every detail matters";
    if (acceptanceRate <= 20) return "highly selective institution with rigorous standards";
    if (acceptanceRate <= 35) return "selective college with competitive admissions";
    if (acceptanceRate <= 60) return "moderately selective institution";
    return "accessible institution where fit matters more than perfect scores";
  };

  const getGPABreakdown = (collegeName, userProfile, collegeStats) => {
    const userGPA = userProfile.gpa;
    const classRank = userProfile.classRank;
    const acceptanceRate = collegeStats.acceptanceRate;
    const p25 = collegeStats.gpa?.p25;
    const p75 = collegeStats.gpa?.p75;

    if (!userGPA) {
      return `
# 📊 GPA Analysis for ${collegeName}

## Current Standing
No GPA data provided - Add your GPA to see a detailed analysis.

### Why GPA Matters
Your GPA is one of the most important factors in college admissions, representing your academic performance over time.

## 💡 Next Steps
1. Add Your GPA in the profile section
2. Include Course Rigor - AP, IB, and honors courses matter
3. Consider Weighted vs Unweighted - Many colleges recalculate GPAs
4. Show Growth - Upward trends are viewed positively

---
## 🎓 Bottom Line
Complete your profile to get personalized GPA insights for ${collegeName}.
      `;
    }

    const position = getPositionDescription(userGPA, p25, p75, 'GPA');
    const gpaContext = getGPAContext(userGPA);
    const selectivityContext = getCollegeSelectivityContext(acceptanceRate);

    let analysis = `
# 📊 Your GPA Analysis for ${collegeName}

## Current Standing
Your ${userGPA} GPA places you in the ${position} range among admitted students at ${collegeName}.

### 📈 Percentile Positioning
`;

    if (p25 && p75) {
      analysis += `- College's 25th percentile: ${p25}
- College's 75th percentile: ${p75}
- Your position: ${userGPA >= p75 ? 'Above 75th percentile' : userGPA >= p25 ? 'Within middle 50%' : 'Below 25th percentile'}

`;
    }

    analysis += `## 🎯 Competitive Context
At ${collegeName} with its ${acceptanceRate}% acceptance rate, you're in a ${selectivityContext}.

### What This GPA Means
Your GPA ${gpaContext} and `;

    if (userGPA >= p75) {
      analysis += `is a significant strength in your application. This demonstrates exceptional academic consistency that admissions officers highly value.
`;
    } else if (userGPA >= p25) {
      analysis += `shows you're academically competitive for this institution. You'll need other strengths to stand out in this pool.
`;
    } else {
      analysis += `is below their typical range, but strong test scores, activities, and essays can help compensate.
`;
    }

    // Class Rank Context
    if (classRank) {
      analysis += `
### Class Standing Context
Class Rank: Top ${classRank}% - `;
      if (classRank <= 10) {
        analysis += `This exceptional ranking significantly strengthens your application and shows you're among the top performers at your school.
`;
      } else if (classRank <= 25) {
        analysis += `This solid ranking demonstrates strong academic performance relative to your peers.
`;
      } else {
        analysis += `Focus on showcasing growth, leadership, and other strengths in your application.
`;
      }
    }

    // Course Rigor and Trends
    analysis += `
### Academic Context Considerations
- Course Rigor: Colleges value challenging coursework (AP, IB, Honors)
- Grade Trends: Upward trajectories are viewed positively
- School Context: Your GPA is evaluated within your school's academic environment
- Weighted vs Unweighted: Many colleges recalculate GPAs using their own formula

## 💡 Strategic Recommendations
`;

    if (userGPA >= p75) {
      analysis += `
### ⚡ Immediate Actions
- Maintain Excellence: Keep your strong academic performance
- Course Selection: Continue taking challenging courses you can excel in
- Leadership: Focus on extracurricular leadership and impact

### 📚 Long-term Strategy
- Essay Focus: Craft compelling essays that showcase your personality
- Recommendations: Secure strong letters from teachers who know you well
- Demonstrated Interest: Show genuine enthusiasm for ${collegeName}
`;
    } else if (userGPA >= p25) {
      analysis += `
### ⚡ Immediate Actions
- Grade Improvement: Aim for A's in remaining courses
- Course Selection: Take challenging courses you can handle
- Test Preparation: Focus on strong SAT/ACT scores

### 📚 Long-term Strategy
- Essay Excellence: Compelling essays can help offset GPA concerns
- Activity Leadership: Take on leadership roles in extracurriculars
- Early Application: Consider early action/decision if available
`;
    } else {
      analysis += `
### ⚡ Urgent GPA Recovery
Your ${userGPA} puts you at a disadvantage, but senior year matters:
- Target: Aim for all A's in remaining courses
- Course Selection: Take challenging courses you can excel in
- Explanation: Consider addressing context in additional info section

### 📚 Long-term Strategy
- Test Scores: Strong SAT/ACT scores can help compensate
- Activities: Exceptional extracurricular impact is crucial
- Essays: Compelling personal story can make a difference
- Alternative Paths: Consider transfer options or gap year strategies
`;
    }

    analysis += `
---
## 🎓 Bottom Line
Your ${userGPA} GPA ${userGPA >= p75 ? 'is a major strength' : userGPA >= p25 ? 'is competitive but needs support' : 'requires strong other components'} for admission to ${collegeName}. Focus on what you can control and showcase your unique strengths.

Remember: GPA is just one component of your holistic application
`;

    return analysis;
  };

  const getSATBreakdown = (collegeName, userProfile, collegeStats) => {
    const userSAT = userProfile.sat;
    const acceptanceRate = collegeStats.acceptanceRate;
    const p25 = collegeStats.sat?.p25;
    const p75 = collegeStats.sat?.p75;

    if (!userSAT) {
      return `
# 📝 SAT Analysis for ${collegeName}

## Current Standing
No SAT score provided - Add your SAT score to see a detailed analysis.

### Why SAT Matters
The SAT measures critical reading, writing, and mathematical reasoning skills that colleges use to predict academic success.

## 💡 Next Steps
1. Add Your SAT Score in the profile section
2. Consider Section Breakdown - Math vs Evidence-Based Reading & Writing
3. Research Superscoring - Many colleges combine your best sections
4. Evaluate Test-Optional - Some schools don't require SAT scores

---
## 🎓 Bottom Line
Complete your profile to get personalized SAT insights for ${collegeName}.
      `;
    }

    const position = getPositionDescription(userSAT, p25, p75, 'SAT');
    const testContext = getTestContext(userSAT, 'SAT');
    const selectivityContext = getCollegeSelectivityContext(acceptanceRate);

    // Calculate section scores (assuming even split for now)
    const mathScore = Math.round(userSAT * 0.5);
    const ebrwScore = Math.round(userSAT * 0.5);

    let analysis = `
# 📝 Your SAT Analysis for ${collegeName}

## Current Standing
Your ${userSAT} SAT score places you in the ${position} range among admitted students at ${collegeName}.

### 📈 Percentile Positioning
`;

    if (p25 && p75) {
      analysis += `- College's 25th percentile: ${p25}
- College's 75th percentile: ${p75}
- Your position: ${userSAT >= p75 ? 'Above 75th percentile' : userSAT >= p25 ? 'Within middle 50%' : 'Below 25th percentile'}

`;
    }

    analysis += `## 🎯 Competitive Context
At ${collegeName} with its ${acceptanceRate}% acceptance rate, you're in a ${selectivityContext}.

### What This Score Means
Your SAT score ${testContext} and `;

    if (userSAT >= p75) {
      analysis += `is a major strength in your application. This exceptional score demonstrates strong reasoning and problem-solving abilities.
`;
    } else if (userSAT >= p25) {
      analysis += `shows you're competitive for this institution. You can handle the academic rigor expected.
`;
    } else {
      analysis += `is below their typical range, but other application components can help compensate.
`;
    }

    // Section Analysis
    analysis += `
### Section Breakdown
Estimated Section Scores:
- Math: ${mathScore} (800 max)
- Evidence-Based Reading & Writing: ${ebrwScore} (800 max)

Note: These are estimates based on your composite score. Actual section scores may vary.

### Score Range Context
- 1500-1600: Elite range (99th+ percentile)
- 1400-1499: Excellent range (94th-98th percentile)
- 1300-1399: Very Good range (87th-93rd percentile)
- 1200-1299: Good range (75th-86th percentile)
- 1000-1199: Average range (50th-74th percentile)

## 💡 Strategic Recommendations
`;

    if (userSAT >= p75) {
      analysis += `
### ⚡ Immediate Actions
- Maintain Score: Your score is already competitive
- Focus Elsewhere: Prioritize essays, activities, and recommendations
- Superscoring: Consider retaking only if you can improve significantly

### 📚 Long-term Strategy
- Essay Excellence: Craft compelling personal statements
- Activity Leadership: Take on meaningful leadership roles
- Demonstrated Interest: Show genuine enthusiasm for ${collegeName}
`;
    } else if (userSAT >= p25) {
      analysis += `
### ⚡ Immediate Actions
- Evaluate Retake: Consider retaking if you can improve 50+ points
- Section Focus: Identify and improve your weaker section
- Test Prep: Use official SAT practice materials

### 📚 Long-term Strategy
- Superscoring: Many colleges combine your best section scores
- Test-Optional: Research if ${collegeName} is test-optional
- Alternative Tests: Consider ACT if you perform better on it
`;
    } else {
      analysis += `
### ⚡ Urgent Score Improvement
Your ${userSAT} needs attention for ${collegeName}:
- Retake Strategy: Plan for 2-3 more attempts with focused prep
- Section Analysis: Identify specific weaknesses (Math vs EBRW)
- Test Prep: Invest in quality preparation resources

### 📚 Long-term Strategy
- Test-Optional: Consider not submitting if below 25th percentile
- Alternative Paths: Explore test-optional colleges or transfer options
- Holistic Focus: Strengthen other application components significantly
`;
    }

    analysis += `
### Additional Considerations
- Superscoring: Most colleges combine your best section scores across multiple test dates
- Test-Optional: Some schools don't require SAT scores - research ${collegeName}'s policy
- SAT Subject Tests: Consider if relevant to your intended major
- Timing: Plan test dates to allow for retakes if needed

---
## 🎓 Bottom Line
Your ${userSAT} SAT score ${userSAT >= p75 ? 'strengthens your application significantly' : userSAT >= p25 ? 'is competitive but could be improved' : 'requires improvement or alternative strategies'} for admission to ${collegeName}.

Remember: SAT scores are just one component of your holistic application
`;

    return analysis;
  };

  const getACTBreakdown = (collegeName, userProfile, collegeStats) => {
    const userACT = userProfile.act;
    const acceptanceRate = collegeStats.acceptanceRate;
    const p25 = collegeStats.act?.p25;
    const p75 = collegeStats.act?.p75;

    if (!userACT) {
      return `
# 🎯 ACT Analysis for ${collegeName}

## Current Standing
No ACT score provided - Add your ACT score to see a detailed analysis.

### Why ACT Matters
The ACT measures academic achievement in English, Math, Reading, and Science, providing colleges with a comprehensive view of your academic readiness.

## 💡 Next Steps
1. Add Your ACT Score in the profile section
2. Consider Section Breakdown - English, Math, Reading, Science
3. Research Superscoring - Many colleges combine your best sections
4. Compare with SAT - Determine which test better represents your abilities

---
## 🎓 Bottom Line
Complete your profile to get personalized ACT insights for ${collegeName}.
      `;
    }

    const position = getPositionDescription(userACT, p25, p75, 'ACT');
    const testContext = getTestContext(userACT, 'ACT');
    const selectivityContext = getCollegeSelectivityContext(acceptanceRate);

    // Calculate section scores (assuming even split for now)
    const englishScore = Math.round(userACT * 0.25);
    const mathScore = Math.round(userACT * 0.25);
    const readingScore = Math.round(userACT * 0.25);
    const scienceScore = Math.round(userACT * 0.25);

    let analysis = `
# 🎯 Your ACT Analysis for ${collegeName}

## Current Standing
Your ${userACT} ACT score places you in the ${position} range among admitted students at ${collegeName}.

### 📈 Percentile Positioning
`;

    if (p25 && p75) {
      analysis += `- College's 25th percentile: ${p25}
- College's 75th percentile: ${p75}
- Your position: ${userACT >= p75 ? 'Above 75th percentile' : userACT >= p25 ? 'Within middle 50%' : 'Below 25th percentile'}

`;
    }

    analysis += `## 🎯 Competitive Context
At ${collegeName} with its ${acceptanceRate}% acceptance rate, you're in a ${selectivityContext}.

### What This Score Means
Your ACT score ${testContext} and `;

    if (userACT >= p75) {
      analysis += `is a major strength in your application. This outstanding score demonstrates exceptional academic readiness.
`;
    } else if (userACT >= p25) {
      analysis += `shows you're competitive for this institution. You can handle the academic rigor expected.
`;
    } else {
      analysis += `is below their typical range, but other application components can help compensate.
`;
    }

    // Section Analysis
    analysis += `
### Section Breakdown
Estimated Section Scores:
- English: ${englishScore} (36 max)
- Math: ${mathScore} (36 max)
- Reading: ${readingScore} (36 max)
- Science: ${scienceScore} (36 max)

Note: These are estimates based on your composite score. Actual section scores may vary.

### Score Range Context
- 34-36: Elite range (99th+ percentile)
- 31-33: Excellent range (95th-98th percentile)
- 28-30: Very Good range (88th-94th percentile)
- 25-27: Good range (79th-87th percentile)
- 21-24: Average range (50th-78th percentile)

## 💡 Strategic Recommendations
`;

    if (userACT >= p75) {
      analysis += `
### ⚡ Immediate Actions
- Maintain Score: Your score is already competitive
- Focus Elsewhere: Prioritize essays, activities, and recommendations
- Superscoring: Consider retaking only if you can improve significantly

### 📚 Long-term Strategy
- Essay Excellence: Craft compelling personal statements
- Activity Leadership: Take on meaningful leadership roles
- Demonstrated Interest: Show genuine enthusiasm for ${collegeName}
`;
    } else if (userACT >= p25) {
      analysis += `
### ⚡ Immediate Actions
- Evaluate Retake: Consider retaking if you can improve 2+ points
- Section Focus: Identify and improve your weakest section
- Test Prep: Use official ACT practice materials

### 📚 Long-term Strategy
- Superscoring: Many colleges combine your best section scores
- Test-Optional: Research if ${collegeName} is test-optional
- SAT Comparison: Consider SAT if you perform better on it
`;
    } else {
      analysis += `
### ⚡ Urgent Score Improvement
Your ${userACT} needs attention for ${collegeName}:
- Retake Strategy: Plan for 2-3 more attempts with focused prep
- Section Analysis: Identify specific weaknesses across all four sections
- Test Prep: Invest in quality preparation resources

### 📚 Long-term Strategy
- Test-Optional: Consider not submitting if below 25th percentile
- Alternative Paths: Explore test-optional colleges or transfer options
- Holistic Focus: Strengthen other application components significantly
`;
    }

    analysis += `
### Additional Considerations
- Superscoring: Most colleges combine your best section scores across multiple test dates
- Test-Optional: Some schools don't require ACT scores - research ${collegeName}'s policy
- SAT vs ACT: Consider which test better represents your abilities
- Writing Section: Some colleges require the optional writing section
- Timing: Plan test dates to allow for retakes if needed

---
## 🎓 Bottom Line
Your ${userACT} ACT score ${userACT >= p75 ? 'strengthens your application significantly' : userACT >= p25 ? 'is competitive but could be improved' : 'requires improvement or alternative strategies'} for admission to ${collegeName}.

Remember: ACT scores are just one component of your holistic application
`;

    return analysis;
  };

  const getShortSuggestions = (collegeName, statName) => {
    // Use the new focused functions for GPA, SAT, and ACT
    if (statName === 'GPA') {
      return {
        content: getGPABreakdown(collegeName, userProfile, foundCollege.stats),
        suggestions: [
          'Focus on maintaining or improving grades in challenging courses',
          'Consider taking additional AP or honors courses if available',
          'Seek help from teachers or tutors in subjects where you struggle',
          'Maintain consistency across all subjects rather than excelling in just a few'
        ]
      };
    } else if (statName === 'SAT') {
      return {
        content: getSATBreakdown(collegeName, userProfile, foundCollege.stats),
        suggestions: [
          'Consider retaking the SAT if you\'re below the 25th percentile',
          'Focus on your weaker section (Math vs. Evidence-Based Reading)',
          'Take practice tests under timed conditions',
          'Consider SAT Subject Tests if they align with your intended major'
        ]
      };
    } else if (statName === 'ACT') {
      return {
        content: getACTBreakdown(collegeName, userProfile, foundCollege.stats),
        suggestions: [
          'Retake the ACT if you\'re significantly below the middle 50% range',
          'Focus on your lowest-scoring section for maximum improvement',
          'Practice time management strategies for each section',
          'Consider the ACT if your score is higher than your SAT equivalent'
        ]
      };
    }

    // Fallback for other metrics
    return {
      content: `
# ${statName} Analysis for ${collegeName}

## Overview
This metric is an important component of your application profile for ${collegeName}.

## Current Assessment
Your performance in this area contributes to how admissions officers evaluate your overall fit and potential success.

## Areas for Growth
Every aspect of your application can be strengthened with focused effort and strategic planning.
      `,
      suggestions: [
        'Research specific requirements and preferences for this metric',
        'Identify concrete steps you can take to improve in this area',
        'Consider how this connects to your overall application narrative',
        'Seek guidance from counselors or mentors familiar with college admissions'
      ]
    };
  };

  // Announce changes to screen readers
  const announceToScreenReader = (message) => {
    const liveRegion = document.getElementById('academic-comparison-live-region');
    if (liveRegion) {
      liveRegion.textContent = message;
      setTimeout(() => {
        liveRegion.textContent = '';
      }, 1000);
    }
  };

  useEffect(() => {
    const fetchAnalytics = async () => {
      // Skip if no college found
      if (!foundCollege) return;
      
      // Make sure we have valid user profile data before making the request
      if (!userProfile.gpa && !userProfile.sat && !userProfile.act) {
        setAnalysis(null);
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        const collegeActivities = JSON.parse(localStorage.getItem('collegeActivities')) || [];

        // Helper function to ensure string values or null
        const toStringOrNull = (value) => {
          if (value === null || value === undefined || value === '') {
            return null;
          }
          return String(value);
        };

        const response = await fetch(`${API_BASE_URL}/analytics/personalized`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userProfile: {
              gpa: toStringOrNull(userProfile?.gpa),
              sat: toStringOrNull(userProfile?.sat),
              act: toStringOrNull(userProfile?.act),
              classRank: toStringOrNull(userProfile?.classRank),
              gender: toStringOrNull(userProfile?.gender),
              ethnicity: toStringOrNull(userProfile?.ethnicity),
            },
            activities: collegeActivities
              .filter(activity => activity && (activity.title || activity.name)) // Filter out invalid activities
              .map(activity => ({
                name: String(activity.title || activity.name || 'Unnamed Activity'),
                organization: toStringOrNull(activity.organization),
                description: String(activity.description || ''),
                hoursPerWeek: toStringOrNull(activity.hoursPerWeek),
                weeksPerYear: toStringOrNull(activity.weeksPerYear),
                leadershipRole: Number(activity.leadershipRole) || 0,
                activityTier: Number(activity.activityTier) || 0
              })),
            collegeName: String(foundCollege.name),
            collegeStats: {
              acceptanceRate: toStringOrNull(foundCollege.stats?.acceptanceRate),
              satRange: toStringOrNull(foundCollege.stats?.satRange),
              actRange: toStringOrNull(foundCollege.stats?.actRange),
              studentFacultyRatio: toStringOrNull(foundCollege.stats?.studentFacultyRatio)
            }
          }),
        });

        if (!response.ok) {
          if (response.status === 422) {
            // Log the request data for debugging validation errors
            const requestData = {
              userProfile: {
                gpa: toStringOrNull(userProfile?.gpa),
                sat: toStringOrNull(userProfile?.sat),
                act: toStringOrNull(userProfile?.act),
                classRank: toStringOrNull(userProfile?.classRank),
                gender: toStringOrNull(userProfile?.gender),
                ethnicity: toStringOrNull(userProfile?.ethnicity),
              },
              activities: collegeActivities
                .filter(activity => activity && (activity.title || activity.name))
                .map(activity => ({
                  name: String(activity.title || activity.name || 'Unnamed Activity'),
                  organization: toStringOrNull(activity.organization),
                  description: String(activity.description || ''),
                  hoursPerWeek: toStringOrNull(activity.hoursPerWeek),
                  weeksPerYear: toStringOrNull(activity.weeksPerYear),
                  leadershipRole: Number(activity.leadershipRole) || 0,
                  activityTier: Number(activity.activityTier) || 0
                })),
              collegeName: String(foundCollege.name),
              collegeStats: {
                acceptanceRate: toStringOrNull(foundCollege.stats?.acceptanceRate),
                satRange: toStringOrNull(foundCollege.stats?.satRange),
                actRange: toStringOrNull(foundCollege.stats?.actRange),
                studentFacultyRatio: toStringOrNull(foundCollege.stats?.studentFacultyRatio)
              }
            };
            console.error('422 Validation Error - Request data:', requestData);
            const errorText = await response.text();
            console.error('422 Validation Error - Response:', errorText);
            throw new Error(`Validation error: ${errorText}`);
          }
          throw new Error('Failed to fetch analytics');
        }

        const data = await response.json();
        setAnalysis(data);
        
        // Announce to screen readers when data loads
        announceToScreenReader(`Analytics loaded for ${foundCollege.name}`);
      } catch (err) {
        console.error('Error fetching analytics:', err);
        setError(err.message);
        announceToScreenReader('Error loading analytics data');
      } finally {
        setIsLoading(false);
      }
    };

    // Only fetch if we have foundCollege and some user profile data
    if (foundCollege && (userProfile.gpa || userProfile.sat || userProfile.act)) {
      fetchAnalytics();
    } else {
      setIsLoading(false);
    }

    // Listen for activity updates
    const handleActivitiesUpdate = (event) => {
      console.log('Activities updated, refetching analytics...');
      fetchAnalytics();
    };

    window.addEventListener('collegeActivitiesUpdated', handleActivitiesUpdate);
    
    return () => {
      window.removeEventListener('collegeActivitiesUpdated', handleActivitiesUpdate);
    };
  }, [foundCollege?.name, userProfile.gpa, userProfile.sat, userProfile.act, userProfile.classRank, userProfile.ethnicity, userProfile.gender]);

  // Initialize tooltips and accessibility features
  useEffect(() => {
    // Add keyboard navigation support
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && breakdownModal.open) {
        setBreakdownModal({ open: false, stat: null, content: '', suggestions: null });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    // Add ARIA live region for dynamic content updates
    const liveRegion = document.createElement('div');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.style.position = 'absolute';
    liveRegion.style.left = '-10000px';
    liveRegion.style.width = '1px';
    liveRegion.style.height = '1px';
    liveRegion.style.overflow = 'hidden';
    liveRegion.id = 'academic-comparison-live-region';
    document.body.appendChild(liveRegion);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const existingLiveRegion = document.getElementById('academic-comparison-live-region');
      if (existingLiveRegion) {
        document.body.removeChild(existingLiveRegion);
      }
    };
  }, [breakdownModal.open]);

  // Listen for changes to academicProfile in localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'academicProfile') {
        const newProfile = JSON.parse(localStorage.getItem('academicProfile')) || {};
        setUserProfile(newProfile);
      }
    };

    // Listen for storage events from other tabs
    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom events from the same tab
    const handleProfileUpdate = () => {
      const newProfile = JSON.parse(localStorage.getItem('academicProfile')) || {};
      setUserProfile(newProfile);
    };

    window.addEventListener('academicProfileUpdated', handleProfileUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('academicProfileUpdated', handleProfileUpdate);
    };
  }, []);

  // Early return if college not found - AFTER all hooks
  if (!foundCollege) {
    return <div className={styles.container}><p>College not found.</p></div>;
  }

  // Restore statData calculation
  const prepareStatData = () => {
    const stats = [];
    const safeParseFloat = (value) => {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? null : parsed;
    };
    const parseRange = (rangeString) => {
      if (!rangeString || typeof rangeString !== 'string') return null;
      const match = rangeString.match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/);
      if (!match) return null;
      return { min: parseFloat(match[1]), max: parseFloat(match[2]) };
    };
    
    // GPA
    if (userProfile?.gpa && foundCollege.stats.gpa) {
      const userGPA = safeParseFloat(userProfile.gpa);
      let userPosition = 0;
      if (!isNaN(userGPA)) {
        userPosition = Math.max(0, Math.min(100, (userGPA / 4.0) * 100));
      }
      
      // Parse GPA percentiles from college data
      const schoolMin = safeParseFloat(foundCollege.stats.gpa.p25);
      const schoolMax = safeParseFloat(foundCollege.stats.gpa.p75);
      const schoolMedian = (!isNaN(schoolMin) && !isNaN(schoolMax)) ? (schoolMin + schoolMax) / 2 : null;
      
      stats.push({
        name: 'GPA',
        userScore: isNaN(userGPA) ? '' : userGPA.toFixed(2),
        userValue: userGPA,
        medianValue: schoolMedian,
        schoolMin: isNaN(schoolMin) ? '' : schoolMin.toFixed(2),
        schoolMax: isNaN(schoolMax) ? '' : schoolMax.toFixed(2),
        schoolMedian: isNaN(schoolMedian) ? '' : schoolMedian.toFixed(2),
        userPosition,
        color: '#2196F3',
        unit: ''
      });
    }
    // SAT
    if (userProfile?.sat && foundCollege.stats.sat) {
      const userSAT = safeParseFloat(userProfile.sat);
      let userPosition = 0;
      if (!isNaN(userSAT)) {
        userPosition = Math.max(0, Math.min(100, ((userSAT - 400) / (1600 - 400)) * 100));
      }
      
      // Parse SAT percentiles from college data  
      const schoolMin = safeParseFloat(foundCollege.stats.sat.p25);
      const schoolMax = safeParseFloat(foundCollege.stats.sat.p75);
      const schoolMedian = (!isNaN(schoolMin) && !isNaN(schoolMax)) ? Math.round((schoolMin + schoolMax) / 2) : null;
      
      stats.push({
        name: 'SAT',
        userScore: isNaN(userSAT) ? '' : userSAT.toString(),
        userValue: userSAT,
        medianValue: schoolMedian,
        schoolMin: isNaN(schoolMin) ? '' : schoolMin.toString(),
        schoolMax: isNaN(schoolMax) ? '' : schoolMax.toString(),
        schoolMedian: isNaN(schoolMedian) ? '' : schoolMedian.toString(),
        userPosition,
        color: '#4CAF50',
        unit: ''
      });
    }
    // ACT
    if (userProfile?.act && foundCollege.stats.act) {
      const userACT = safeParseFloat(userProfile.act);
      let userPosition = 0;
      if (!isNaN(userACT)) {
        userPosition = Math.max(0, Math.min(100, ((userACT - 1) / (36 - 1)) * 100));
      }
      
      // Parse ACT percentiles from college data
      const schoolMin = safeParseFloat(foundCollege.stats.act.p25);
      const schoolMax = safeParseFloat(foundCollege.stats.act.p75); 
      const schoolMedian = (!isNaN(schoolMin) && !isNaN(schoolMax)) ? Math.round((schoolMin + schoolMax) / 2) : null;
      
      stats.push({
        name: 'ACT',
        userScore: isNaN(userACT) ? '' : userACT.toString(),
        userValue: userACT,
        medianValue: schoolMedian,
        schoolMin: isNaN(schoolMin) ? '' : schoolMin.toString(),
        schoolMax: isNaN(schoolMax) ? '' : schoolMax.toString(),
        schoolMedian: isNaN(schoolMedian) ? '' : schoolMedian.toString(),
        userPosition,
        color: '#FF9800',
        unit: ''
      });
    }
    return stats;
  };
  const statData = prepareStatData();

  // Prepare radar chart metrics data
  const prepareRadarMetrics = () => {
    const metrics = [];
    const safeParseFloat = (value) => {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    };
    const parseRange = (rangeString) => {
      if (!rangeString || typeof rangeString !== 'string') return null;
      const parts = rangeString.split('-');
      if (parts.length !== 2) return null;
      const min = safeParseFloat(parts[0]);
      const max = safeParseFloat(parts[1]);
      return min < max ? { min, max } : null;
    };
    // GPA
    let userGPA = userProfile.gpa ? safeParseFloat(userProfile.gpa) : 0;
    let minGPA = (foundCollege.stats.gpa && foundCollege.stats.gpa.p25) ? safeParseFloat(foundCollege.stats.gpa.p25) : 0;
    let maxGPA = (foundCollege.stats.gpa && foundCollege.stats.gpa.p75) ? safeParseFloat(foundCollege.stats.gpa.p75) : 4.0;
    metrics.push({
      name: 'GPA',
      userValue: userGPA,
      min: minGPA,  // 25th percentile for background indicator
      max: maxGPA   // 75th percentile for background indicator
    });
    // SAT
    let userSAT = userProfile.sat ? safeParseFloat(userProfile.sat) : 0;
    let minSAT = (foundCollege.stats.sat && foundCollege.stats.sat.p25) ? safeParseFloat(foundCollege.stats.sat.p25) : 400;
    let maxSAT = (foundCollege.stats.sat && foundCollege.stats.sat.p75) ? safeParseFloat(foundCollege.stats.sat.p75) : 1600;
    metrics.push({
      name: 'SAT',
      userValue: userSAT,
      min: minSAT,  // 25th percentile for background indicator
      max: maxSAT   // 75th percentile for background indicator
    });
    // ACT
    let userACT = userProfile.act ? safeParseFloat(userProfile.act) : 0;
    let minACT = (foundCollege.stats.act && foundCollege.stats.act.p25) ? safeParseFloat(foundCollege.stats.act.p25) : 1;
    let maxACT = (foundCollege.stats.act && foundCollege.stats.act.p75) ? safeParseFloat(foundCollege.stats.act.p75) : 36;
    metrics.push({
      name: 'ACT',
      userValue: userACT,
      min: minACT,  // 25th percentile for background indicator
      max: maxACT   // 75th percentile for background indicator
    });
    // Class Rank
    let userRank = userProfile.classRank ? safeParseFloat(userProfile.classRank) : (userGPA >= 3.8 ? 15 : userGPA >= 3.5 ? 35 : 60);
    metrics.push({
      name: 'Class Rank',
      userValue: userRank,
      min: 5,   // 25th percentile for background indicator (top 5%)
      max: 25   // 75th percentile for background indicator (top 25%)
    });
    // Extracurricular
    const collegeActivities = JSON.parse(localStorage.getItem('collegeActivities')) || [];
    let activityStrength = 0;
    if (collegeActivities.length > 0) {
      const avgTier = collegeActivities.reduce((sum, act) => sum + (act.activityTier || 3), 0) / collegeActivities.length;
      activityStrength = Math.min(10, Math.max(0, collegeActivities.length * 1.5 + (5 - avgTier)));
      if (isNaN(activityStrength)) activityStrength = 0;
    }
    metrics.push({
      name: 'Extracurricular',
      userValue: activityStrength,
      min: 3,   // Minimum activity strength for background indicator
      max: 10   // Maximum activity strength for background indicator
    });
    return metrics;
  };

  const radarMetrics = prepareRadarMetrics();

  // Prepare deadline timeline data
  const prepareDeadlineData = () => {
    // For now, create sample deadline data for the current college
    // In a real app, this would come from a database of college deadlines
    const currentYear = new Date().getFullYear();
    const nextYear = currentYear + 1;
    
    const sampleDeadlines = [
      {
        label: `${foundCollege.name} Early Action`,
        date: `${currentYear}-11-01`,
        type: 'early'
      },
      {
        label: `${foundCollege.name} Regular Decision`,
        date: `${nextYear}-01-15`,
        type: 'regular'
      },
      {
        label: `${foundCollege.name} Financial Aid (FAFSA)`,
        date: `${nextYear}-02-15`,
        type: 'financialAid'
      }
    ];

    // Add some variation based on college type and selectivity
    const acceptanceRate = parseFloat(foundCollege.stats.acceptanceRate) || 50;
    
    if (acceptanceRate < 10) {
      // Highly selective schools often have earlier deadlines
      sampleDeadlines[0].date = `${currentYear}-10-15`; // Earlier EA
      sampleDeadlines[1].date = `${nextYear}-01-01`;    // Earlier RD
    } else if (acceptanceRate > 30) {
      // Less selective schools may have later deadlines
      sampleDeadlines[0].date = `${currentYear}-11-15`; // Later EA
      sampleDeadlines[1].date = `${nextYear}-02-01`;    // Later RD
    }

    return sampleDeadlines;
  };

  const deadlineData = prepareDeadlineData();

  // Enhanced legend with better design and accessibility
  const StatLegend = () => {
    const [expandedSections, setExpandedSections] = useState({
      scoreBreakdowns: false,
      interpretationGuide: false,
      proTips: false
    });

    const toggleSection = (section) => {
      setExpandedSections(prev => ({
        ...prev,
        [section]: !prev[section]
      }));
    };

    return (
      <div className={styles.statLegend} role="region" aria-label="Chart legend">
        <h4 className={styles.legendTitle}>Understanding Your Academic Profile</h4>
        
        {/* Always Visible - Basic Legend Items */}
        <div className={styles.legendItems}>
          <div className={styles.legendItem}>
            <div className={styles.legendIndicator} style={{ 
              background: 'linear-gradient(90deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.25) 100%)',
              border: '2px solid rgba(59, 130, 246, 0.4)',
              width: 40,
              height: 16,
              borderRadius: 8
            }} />
            <div className={styles.legendContent}>
              <span className={styles.legendLabel}>School Range (Middle 50%)</span>
              <p className={styles.legendDescription}>
                The blue band shows where 50% of admitted students scored (25th-75th percentile). 
                This is the "typical" range for this school.
              </p>
            </div>
          </div>
          
          <div className={styles.legendItem}>
            <div className={styles.legendIndicator} style={{ 
              width: 4,
              height: 24,
              background: 'linear-gradient(180deg, #6366F1 0%, #4F46E5 100%)',
              borderRadius: 2,
              boxShadow: '0 2px 4px rgba(99, 102, 241, 0.3)'
            }} />
            <div className={styles.legendContent}>
              <span className={styles.legendLabel}>School Median (50th Percentile)</span>
              <p className={styles.legendDescription}>
                The vertical line shows the median score - exactly half of admitted students scored above 
                and half below this point. This is a key competitive benchmark.
              </p>
            </div>
          </div>
          
          <div className={styles.legendItem}>
            <div className={styles.legendIndicator} style={{ 
              width: 12,
              height: 12,
              background: '#10B981',
              borderRadius: '50%',
              boxShadow: '0 0 0 3px rgba(16, 185, 129, 0.2), 0 2px 8px rgba(16, 185, 129, 0.3)'
            }} />
            <div className={styles.legendContent}>
              <span className={styles.legendLabel}>Your Position</span>
              <p className={styles.legendDescription}>
                Your score's position relative to admitted students. Color indicates competitiveness:
                <span style={{color: '#10B981', fontWeight: 'bold'}}> Green</span> (strong),
                <span style={{color: '#F59E0B', fontWeight: 'bold'}}> Orange</span> (competitive),
                <span style={{color: '#EF4444', fontWeight: 'bold'}}> Red</span> (reach).
              </p>
            </div>
          </div>
        </div>

        {/* Collapsible Section - Score Breakdowns */}
        <div className={styles.collapsibleSection}>
          <button 
            className={styles.sectionToggle}
            onClick={() => toggleSection('scoreBreakdowns')}
            aria-expanded={expandedSections.scoreBreakdowns}
          >
            <span className={styles.toggleIcon}>
              {expandedSections.scoreBreakdowns ? <BookSVG /> : <ChartSVG />}
            </span>
            <span className={styles.toggleTitle}>Detailed Score Breakdowns</span>
            <span className={styles.toggleArrow}>
              {expandedSections.scoreBreakdowns ? '▼' : '▶'}
            </span>
          </button>
          
          <div className={`${styles.collapsibleContent} ${expandedSections.scoreBreakdowns ? styles.expanded : ''}`}>
            <div className={styles.legendSections}>
              <div className={styles.legendSection}>
                <h5 className={styles.sectionTitle}>
                  <ChartSVG />
                  GPA Understanding
                </h5>
                <div className={styles.sectionGrid}>
                  <div className={styles.scoreRange}>
                    <strong>4.0 Scale Breakdown:</strong>
                    <ul>
                      <li><strong>3.8-4.0:</strong> Excellent (mostly A's)</li>
                      <li><strong>3.5-3.79:</strong> Very Good (A's and B's)</li>
                      <li><strong>3.0-3.49:</strong> Good (B's and some C's)</li>
                      <li><strong>Below 3.0:</strong> Needs improvement</li>
                    </ul>
                  </div>
                  <div className={styles.contextInfo}>
                    <strong>What This Means:</strong>
                    <p>Colleges recalculate GPAs using core academic courses. Weighted GPAs may exceed 4.0 
                    due to honors/AP classes. Class rank and school rigor also matter significantly.</p>
                  </div>
                </div>
              </div>

              <div className={styles.legendSection}>
                <h5 className={styles.sectionTitle}>
                  <SATSVG />
                  SAT Score Guide
                </h5>
                <div className={styles.sectionGrid}>
                  <div className={styles.scoreRange}>
                    <strong>Score Ranges (1600 scale):</strong>
                    <ul>
                      <li><strong>1500-1600:</strong> Elite (99th+ percentile)</li>
                      <li><strong>1400-1499:</strong> Excellent (94th-98th percentile)</li>
                      <li><strong>1300-1399:</strong> Very Good (87th-93rd percentile)</li>
                      <li><strong>1200-1299:</strong> Good (75th-86th percentile)</li>
                      <li><strong>1000-1199:</strong> Average (50th-74th percentile)</li>
                    </ul>
                  </div>
                  <div className={styles.contextInfo}>
                    <strong>Section Breakdown:</strong>
                    <p><strong>Math:</strong> 200-800 points | <strong>Reading & Writing:</strong> 200-800 points</p>
                    <p>Most competitive schools look at both sections equally. Consider retaking if you're 
                    below the school's 25th percentile for a meaningful boost.</p>
                  </div>
                </div>
              </div>

              <div className={styles.legendSection}>
                <h5 className={styles.sectionTitle}>
                  <ACTSVG />
                  ACT Score Guide
                </h5>
                <div className={styles.sectionGrid}>
                  <div className={styles.scoreRange}>
                    <strong>Score Ranges (36 scale):</strong>
                    <ul>
                      <li><strong>34-36:</strong> Elite (99th+ percentile)</li>
                      <li><strong>31-33:</strong> Excellent (95th-98th percentile)</li>
                      <li><strong>28-30:</strong> Very Good (88th-94th percentile)</li>
                      <li><strong>25-27:</strong> Good (79th-87th percentile)</li>
                      <li><strong>21-24:</strong> Average (50th-78th percentile)</li>
                    </ul>
                  </div>
                  <div className={styles.contextInfo}>
                    <strong>Section Breakdown:</strong>
                    <p><strong>English, Math, Reading, Science:</strong> Each scored 1-36</p>
                    <p>Composite score is the average of all four sections. Some students perform better 
                    on ACT vs SAT due to different question styles and timing.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Collapsible Section - Interpretation Guide */}
        <div className={styles.collapsibleSection}>
          <button 
            className={styles.sectionToggle}
            onClick={() => toggleSection('interpretationGuide')}
            aria-expanded={expandedSections.interpretationGuide}
          >
            <span className={styles.toggleIcon}><SearchSVG /></span>
            <span className={styles.toggleTitle}>How to Interpret Your Position</span>
            <span className={styles.toggleArrow}>
              {expandedSections.interpretationGuide ? '▼' : '▶'}
            </span>
          </button>
          
          <div className={`${styles.collapsibleContent} ${expandedSections.interpretationGuide ? styles.expanded : ''}`}>
            <div className={styles.interpretationGuide}>
              <div className={styles.interpretationGrid}>
                <div className={styles.interpretationItem}>
                  <div className={styles.interpretationIcon} style={{backgroundColor: 'rgba(16, 185, 129, 0.1)'}}>
                    <span style={{color: '#10B981'}}>💚</span>
                  </div>
                  <div>
                    <strong style={{color: '#10B981'}}>Strong Position (Above 75th percentile)</strong>
                    <p>Your score puts you in the top 25% of admitted students. This is a significant strength 
                    in your application that can help offset weaker areas.</p>
                  </div>
                </div>
                
                <div className={styles.interpretationItem}>
                  <div className={styles.interpretationIcon} style={{backgroundColor: 'rgba(245, 158, 11, 0.1)'}}>
                    <span style={{color: '#F59E0B'}}>🟡</span>
                  </div>
                  <div>
                    <strong style={{color: '#F59E0B'}}>Competitive Position (25th-75th percentile)</strong>
                    <p>You're in the middle 50% range - competitive but not outstanding. Focus on strengthening 
                    other application areas like essays and extracurriculars.</p>
                  </div>
                </div>
                
                <div className={styles.interpretationItem}>
                  <div className={styles.interpretationIcon} style={{backgroundColor: 'rgba(239, 68, 68, 0.1)'}}>
                    <span style={{color: '#EF4444'}}>🔴</span>
                  </div>
                  <div>
                    <strong style={{color: '#EF4444'}}>Reach Position (Below 25th percentile)</strong>
                    <p>This score makes admission more challenging. Consider retaking the test, but also 
                    ensure other application elements (essays, activities, recommendations) are exceptional.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Collapsible Section - Pro Tips */}
        <div className={styles.collapsibleSection}>
          <button 
            className={styles.sectionToggle}
            onClick={() => toggleSection('proTips')}
            aria-expanded={expandedSections.proTips}
          >
            <span className={styles.toggleIcon}><LightbulbSVG /></span>
            <span className={styles.toggleTitle}>Pro Tips & Strategic Advice</span>
            <span className={styles.toggleArrow}>
              {expandedSections.proTips ? '▼' : '▶'}
            </span>
          </button>
          
          <div className={`${styles.collapsibleContent} ${expandedSections.proTips ? styles.expanded : ''}`}>
            <div className={styles.legendNote}>
              <div className={styles.legendNotContent}>
                <strong>Strategic Insights:</strong>
                <ul>
                  <li><strong>Test-Optional Schools:</strong> If you're below the 25th percentile, consider not submitting scores</li>
                  <li><strong>Superscoring:</strong> Many schools take your highest section scores across multiple test dates</li>
                  <li><strong>Context Matters:</strong> Schools consider your scores in context of your school's resources and opportunities</li>
                  <li><strong>Holistic Review:</strong> Strong essays, activities, and recommendations can overcome lower test scores</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const EnhancedStatBar = ({ stat }) => {
    // Hide if user data is missing
    if (stat.userValue === undefined || stat.userValue === null || stat.userValue === '') {
      return (
        <div className={styles.statCard}>
          <div className={styles.statCardHeader}>
            <h3 className={styles.statName}>{stat.name}</h3>
          </div>
          <div className={styles.statCardContent}>
            <div className={styles.noDataMessage}>
              <span className={styles.noDataIcon}>📊</span>
              <p>No data available</p>
              <small>Add your {stat.name} score to see comparison</small>
            </div>
          </div>
        </div>
      );
    }

    // Parse percentiles
    const min = stat.name === 'GPA' ? 0 : stat.name === 'SAT' ? 400 : 1;
    const max = stat.name === 'GPA' ? 4 : stat.name === 'SAT' ? 1600 : 36;
    const p25 = Number(stat.schoolMin);
    const p75 = Number(stat.schoolMax);
    const median = Number(stat.medianValue);
    const user = Number(stat.userValue);
    
    // Clamp user and median to min/max
    const clamp = (v) => Math.max(min, Math.min(v, max));
    const percent = v => ((v - min) / (max - min)) * 100;
    
    const minPos = 0;
    const p25Pos = percent(p25);
    const medianPos = percent(clamp(median));
    const p75Pos = percent(p75);
    const maxPos = 100;
    const userPos = percent(clamp(user));
    
    // Determine user's position category and colors
    const getUserPositionData = () => {
      if (user >= p75) {
        return {
          category: 'strong',
          color: '#10B981', // Emerald green
          bgColor: 'rgba(16, 185, 129, 0.1)',
          borderColor: 'rgba(16, 185, 129, 0.3)',
          label: 'Strong Range',
          description: 'Above 75th percentile'
        };
      } else if (user >= p25) {
        return {
          category: 'competitive',
          color: '#F59E0B', // Amber
          bgColor: 'rgba(245, 158, 11, 0.1)',
          borderColor: 'rgba(245, 158, 11, 0.3)',
          label: 'Competitive Range',
          description: 'Within middle 50%'
        };
      } else {
        return {
          category: 'reach',
          color: '#EF4444', // Red
          bgColor: 'rgba(239, 68, 68, 0.1)',
          borderColor: 'rgba(239, 68, 68, 0.3)',
          label: 'Reach Range',
          description: 'Below 25th percentile'
        };
      }
    };

    const positionData = getUserPositionData();
    
    // Padding and sizing
    const barHeight = 32;
    const barRadius = 16;
    
    const handleBreakdownClick = () => {
      const suggestionData = getShortSuggestions(foundCollege.name, stat.name);
      setBreakdownModal({
        open: true,
        stat: stat.name,
        content: suggestionData.content,
        suggestions: suggestionData.suggestions
      });
    };

    return (
      <div className={styles.statCard} role="region" aria-labelledby={`${stat.name}-title`}>
        <div className={styles.statCardHeader}>
          <div className={styles.statTitleSection}>
            <h3 id={`${stat.name}-title`} className={styles.statName}>{stat.name}</h3>
            <div className={styles.statBadge} style={{ 
              backgroundColor: positionData.bgColor,
              color: positionData.color,
              borderColor: positionData.borderColor 
            }}>
              {positionData.label}
            </div>
          </div>
          <div className={styles.statValue}>
            <span className={styles.userScore}>{stat.userScore}</span>
            <small className={styles.scoreContext}>{positionData.description}</small>
          </div>
        </div>
        
        <div className={styles.statCardContent}>
          <div className={styles.chartContainer}>
            <div 
              className={styles.chartWrapper}
              role="img"
              aria-label={`${stat.name} comparison chart showing your score of ${stat.userScore} relative to school's range`}
            >
              {/* Background track with gradient */}
              <div className={styles.chartTrack} />
              
              {/* School range (IQR) with intelligent color coding */}
              <div 
                className={styles.schoolRange}
                style={{
                  left: `${p25Pos}%`,
                  width: `${p75Pos - p25Pos}%`,
                  background: `linear-gradient(90deg, 
                    rgba(59, 130, 246, 0.15) 0%, 
                    rgba(59, 130, 246, 0.25) 50%, 
                    rgba(59, 130, 246, 0.15) 100%)`
                }}
                role="presentation"
              />
              
              {/* Range indicators */}
              <div 
                className={styles.rangeIndicator}
                style={{ left: `${p25Pos}%` }}
                data-tooltip="25th percentile"
              />
              <div 
                className={styles.rangeIndicator}
                style={{ left: `${p75Pos}%` }}
                data-tooltip="75th percentile"
              />
              
              {/* Median line */}
              <div
                className={styles.medianLine}
                style={{ left: `${medianPos}%` }}
                data-tooltip={`Median: ${stat.schoolMedian}`}
                role="presentation"
              />
              
              {/* Enhanced user marker */}
              <div
                className={styles.userMarkerEnhanced}
                style={{ 
                  left: `${userPos}%`,
                  backgroundColor: positionData.color,
                  boxShadow: `0 0 0 3px ${positionData.bgColor}, 0 4px 12px ${positionData.color}40`
                }}
                data-tooltip={`Your ${stat.name}: ${stat.userScore}`}
                role="presentation"
              />
              
              {/* Percentile labels */}
              <div className={styles.percentileLabels}>
                <span className={styles.percentileLabel} style={{ left: '0%' }}>
                  {stat.name === 'GPA' ? '0.0' : stat.name === 'SAT' ? '400' : '1'}
                </span>
                <span className={styles.percentileLabel} style={{ left: '25%' }}>
                  25th
                </span>
                <span className={styles.percentileLabel} style={{ left: '50%' }}>
                  50th
                </span>
                <span className={styles.percentileLabel} style={{ left: '75%' }}>
                  75th
                </span>
                <span className={styles.percentileLabel} style={{ right: '0%' }}>
                  Top 1%
                </span>
              </div>
            </div>
            
            {/* Score details */}
            <div className={styles.scoreDetails}>
              <div className={styles.scoreRange}>
                <div className={styles.rangeLabelContainer}>
                  <span className={styles.rangeLabel}>College Range</span>
                  <span className={styles.percentileClarification}>(25th-75th percentile)</span>
                </div>
                <span className={styles.rangeValue}>
                  {stat.schoolMin} - {stat.schoolMax}
                </span>
              </div>
              <div className={styles.scoreScale}>
                <span className={styles.scaleLabel}>Scale</span>
                <span className={styles.scaleValue}>
                  {stat.name === 'GPA' ? '1.0-4.0' : stat.name === 'SAT' ? '400-1600' : stat.name === 'ACT' ? '1-36' : stat.name === 'Class Rank' ? '1-100' : '1-10'}
                </span>
              </div>
              <div className={styles.scoreMedian}>
                <span className={styles.medianLabel}>Median</span>
                <span className={styles.medianValue}>{stat.schoolMedian}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className={styles.statCardActions}>
          <button 
            onClick={handleBreakdownClick} 
            className={styles.breakdownButtonEnhanced}
            aria-label={`View detailed ${stat.name} breakdown and improvement suggestions`}
          >
            <span className={styles.buttonIcon}>{getStatIcon(stat.name)}</span>
            <span>View {stat.name} Breakdown</span>
            <span className={styles.buttonArrow}>→</span>
          </button>
        </div>
      </div>
    );
  };

  // Helper function to parse markdown-like content for modal display
  const parseModalContent = (content) => {
    if (!content) return '';
    
    return content.split('\n').map((line, index) => {
      line = line.trim();
      if (!line) return null;
      
      if (line.startsWith('# ')) {
        return <h1 key={index}>{line.substring(2)}</h1>;
      } else if (line.startsWith('## ')) {
        return <h2 key={index}>{line.substring(3)}</h2>;
      } else if (line.startsWith('### ')) {
        return <h3 key={index}>{line.substring(4)}</h3>;
      } else if (line.startsWith('- **') && line.includes('**:')) {
        const parts = line.substring(2).split('**:');
        const title = parts[0].replace('**', '');
        const content = parts[1];
        return (
          <p key={index}>
            <strong>{title}:</strong>{content}
          </p>
        );
      } else if (line.startsWith('- ')) {
        return <li key={index}>{line.substring(2)}</li>;
      } else {
        return <p key={index}>{line}</p>;
      }
    }).filter(Boolean);
  };

  // Helper to generate a detailed breakdown for admission chance
  const getChanceBreakdown = () => {
    if (!analysis || !foundCollege) return 'No analysis data available.';
    
    const userStats = analysis.userProfile || userProfile || {};
    const collegeName = foundCollege.name;
    const admissionChance = analysis.admissionChance || 0;
    const activities = (analysis.activities || []).length || (JSON.parse(localStorage.getItem('collegeActivities')) || []).length;
    
    // Helper functions for dynamic content generation
    const getAdmissionTier = (chance) => {
      if (chance >= 70) return 'high';
      if (chance >= 45) return 'competitive';
      if (chance >= 25) return 'challenging';
      return 'reach';
    };
    
    const getCollegeSelectivity = (acceptanceRate) => {
      if (acceptanceRate <= 10) return 'extremely selective';
      if (acceptanceRate <= 20) return 'highly selective';
      if (acceptanceRate <= 35) return 'selective';
      if (acceptanceRate <= 50) return 'moderately selective';
      return 'accessible';
    };
    
    const getPercentilePosition = (userValue, p25, p75) => {
      if (!userValue || !p25 || !p75) return null;
      if (userValue >= p75) return 'above 75th percentile';
      if (userValue >= p25) return 'within middle 50%';
      return 'below 25th percentile';
    };
    
    const getAcademicStrength = (gpa, sat, act) => {
      const scores = [];
      if (gpa && gpa >= 3.8) scores.push('strong GPA');
      if (sat && sat >= 1400) scores.push('excellent SAT');
      if (act && act >= 30) scores.push('outstanding ACT');
      return scores.length > 0 ? scores.join(', ') : 'solid academic foundation';
    };
    
    const getActivityQuality = (activityImpacts) => {
      if (!activityImpacts || activityImpacts.length === 0) return 'limited';
      const highImpact = activityImpacts.filter(a => a.impact >= 8).length;
      const mediumImpact = activityImpacts.filter(a => a.impact >= 6 && a.impact < 8).length;
      
      if (highImpact >= 2) return 'exceptional';
      if (highImpact >= 1 || mediumImpact >= 2) return 'strong';
      if (mediumImpact >= 1) return 'decent';
      return 'developing';
    };
    
    // Dynamic formatting functions
    const formatGPA = (gpa, percentile) => {
      if (!gpa) return null;
      if (percentile === 'above 75th percentile') return `Your exceptional ${gpa} GPA`;
      if (percentile === 'within middle 50%') return `Your solid ${gpa} GPA`;
      return `Your ${gpa} GPA`;
    };
    
    const formatTestScore = (score, testName, percentile) => {
      if (!score) return null;
      if (percentile === 'above 75th percentile') return `Your outstanding ${testName} score of ${score}`;
      if (percentile === 'within middle 50%') return `Your competitive ${testName} score of ${score}`;
      return `Your ${testName} score of ${score}`;
    };
    
    const getAcademicHeader = (userStats, foundCollege) => {
      const gpa = userStats.gpa;
      const sat = userStats.sat;
      const act = userStats.act;
      
      let strengths = 0;
      let weaknesses = 0;
      
      if (gpa && foundCollege.stats?.gpa) {
        if (gpa >= foundCollege.stats.gpa.p75) strengths++;
        else if (gpa < foundCollege.stats.gpa.p25) weaknesses++;
      }
      if (sat && foundCollege.stats?.sat) {
        if (sat >= foundCollege.stats.sat.p75) strengths++;
        else if (sat < foundCollege.stats.sat.p25) weaknesses++;
      }
      if (act && foundCollege.stats?.act) {
        if (act >= foundCollege.stats.act.p75) strengths++;
        else if (act < foundCollege.stats.act.p25) weaknesses++;
      }
      
      if (strengths > weaknesses) return `## 📈 Strong Academic Foundation`;
      if (weaknesses > strengths) return `## 📊 Academic Growth Opportunities`;
      return `## 📊 Balanced Academic Profile`;
    };
    
    // Generate dynamic title based on admission chance
    const generateTitle = () => {
      const tier = getAdmissionTier(admissionChance);
      const selectivity = getCollegeSelectivity(foundCollege.stats?.acceptanceRate || 50);
      
      const titles = {
        high: `# 🎯 Strong Contender for ${collegeName}`,
        competitive: `# ⚖️ Competitive Position at ${collegeName}`,
        challenging: `# 🔥 Ambitious Reach for ${collegeName}`,
        reach: `# 🚀 Dream School Challenge: ${collegeName}`
      };
      
      return titles[tier] || titles.competitive;
    };
    
    // Generate academic profile summary
    const generateAcademicSummary = () => {
      const gpa = userStats.gpa;
      const sat = userStats.sat;
      const act = userStats.act;
      const classRank = userStats.classRank;
      
      let summary = `## 📊 Academic Profile Summary\n\n`;
      
      const academicStrength = getAcademicStrength(gpa, sat, act);
      summary += `You present a ${academicStrength} with `;
      
      const metrics = [];
      if (gpa) metrics.push(`GPA: ${gpa}`);
      if (sat) metrics.push(`SAT: ${sat}`);
      if (act) metrics.push(`ACT: ${act}`);
      if (classRank) metrics.push(`Class Rank: Top ${classRank}%`);
      
      summary += metrics.join(', ');
      summary += `.\n\n`;
      
      return summary;
    };
    
    // Generate detailed academic analysis
    const generateAcademicAnalysis = () => {
      const gpa = userStats.gpa;
      const sat = userStats.sat;
      const act = userStats.act;
      const classRank = userStats.classRank;
      
      let analysis = getAcademicHeader(userStats, foundCollege);
      analysis += `\n\n`;
      
      // GPA Analysis
      if (gpa && foundCollege.stats?.gpa) {
        const gpaPercentile = getPercentilePosition(gpa, foundCollege.stats.gpa.p25, foundCollege.stats.gpa.p75);
        if (gpaPercentile) {
          const formattedGPA = formatGPA(gpa, gpaPercentile);
          analysis += `### GPA Analysis\n`;
          analysis += `${formattedGPA} places you ${gpaPercentile} for admitted students at ${collegeName}. `;
          
          if (gpa >= foundCollege.stats.gpa.p75) {
            analysis += `This demonstrates exceptional academic consistency and is a major strength in your application! 📈\n\n`;
          } else if (gpa >= foundCollege.stats.gpa.p25) {
            analysis += `You're academically competitive, but will need other strengths to stand out in this pool. 💪\n\n`;
          } else {
            analysis += `While below their typical range, strong test scores and activities can help compensate. Focus on showcasing growth! 🌱\n\n`;
          }
        }
      }
      
      // Test Score Analysis
      if (sat && foundCollege.stats?.sat) {
        const satPercentile = getPercentilePosition(sat, foundCollege.stats.sat.p25, foundCollege.stats.sat.p75);
        if (satPercentile) {
          const formattedSAT = formatTestScore(sat, 'SAT', satPercentile);
          analysis += `### Standardized Test Performance\n`;
          analysis += `${formattedSAT} is ${satPercentile} for admitted students. `;
          
          if (sat >= foundCollege.stats.sat.p75) {
            analysis += `This exceptional score significantly strengthens your application and demonstrates strong reasoning skills! 🌟\n\n`;
          } else if (sat >= foundCollege.stats.sat.p25) {
            analysis += `This solid score shows you can handle the academic rigor expected at ${collegeName}. 📊\n\n`;
          } else {
            analysis += `Consider retaking if you have time, or focus on showcasing other strengths in your application. 📈\n\n`;
          }
        }
      }
      
      if (act && foundCollege.stats?.act) {
        const actPercentile = getPercentilePosition(act, foundCollege.stats.act.p25, foundCollege.stats.act.p75);
        if (actPercentile) {
          const formattedACT = formatTestScore(act, 'ACT', actPercentile);
          analysis += `${formattedACT} is ${actPercentile} for admitted students. `;
          
          if (act >= foundCollege.stats.act.p75) {
            analysis += `This outstanding score is a major asset and shows exceptional academic readiness! 🎯\n\n`;
          } else if (act >= foundCollege.stats.act.p25) {
            analysis += `This competitive score supports your academic profile well. 📈\n\n`;
          } else {
            analysis += `Focus on other application components to strengthen your candidacy. 🎯\n\n`;
          }
        }
      }
      
      // Class Rank Analysis
      if (classRank) {
        analysis += `### Class Standing\n`;
        analysis += `Class Rank: Top ${classRank}% - `;
        if (classRank <= 10) {
          analysis += `Being in the top 10% of your class is impressive and demonstrates strong academic standing! 🏆\n\n`;
        } else if (classRank <= 25) {
          analysis += `Top quarter ranking shows solid academic performance and competitive positioning. 📚\n\n`;
        } else {
          analysis += `Focus on showcasing growth, leadership, and other strengths in your application. 🌱\n\n`;
        }
      }
      
      return analysis;
    };
    
    // Generate extracurricular impact analysis
    const generateActivityAnalysis = () => {
      let analysis = `## 🌟 Extracurricular Impact\n\n`;
      
      if (analysis.activityImpacts && analysis.activityImpacts.length > 0) {
        const highImpact = analysis.activityImpacts.filter(a => a.impact >= 8);
        const mediumImpact = analysis.activityImpacts.filter(a => a.impact >= 6 && a.impact < 8);
        const activityQuality = getActivityQuality(analysis.activityImpacts);
        
        analysis += `### Leadership & Activities\n`;
        analysis += `Your extracurricular profile shows ${activityQuality} engagement with ${analysis.activityImpacts.length} activities.\n\n`;
        
        if (highImpact.length > 0) {
          analysis += `Top Activities:\n`;
          highImpact.forEach(activity => {
            analysis += `- ${activity.name} (Impact: ${activity.impact}/10) - ${activity.justification} 🌟\n`;
          });
          analysis += `\n`;
        }
        
        if (mediumImpact.length > 0) {
          analysis += `Solid Activities:\n`;
          mediumImpact.forEach(activity => {
            analysis += `- ${activity.name} (Impact: ${activity.impact}/10) - ${activity.justification} 💪\n`;
          });
          analysis += `\n`;
        }
        
        if (highImpact.length === 0 && mediumImpact.length === 0) {
          analysis += `Growth Areas: Consider taking on leadership roles or deepening your involvement to create more impactful experiences. 🚀\n\n`;
        }
      } else if (activities > 0) {
        analysis += `### Current Activities\n`;
        analysis += `You have ${activities} activities listed. Focus on quality over quantity - leadership roles and demonstrated impact matter more than just participation. 📋\n\n`;
      } else {
        analysis += `### Activity Development\n`;
        analysis += `No activities were found in your profile. Adding meaningful extracurriculars with leadership roles and community impact can significantly improve your chances. Consider joining clubs, volunteering, or pursuing personal projects that align with your interests! 🎯\n\n`;
      }
      
      return analysis;
    };
    
    // Generate competitive positioning
    const generateCompetitivePosition = () => {
      let analysis = `## 🎯 Competitive Positioning\n\n`;
      
      const tier = getAdmissionTier(admissionChance);
      const academicStrength = getAcademicStrength(userStats.gpa, userStats.sat, userStats.act);
      const activityQuality = getActivityQuality(analysis.activityImpacts);
      
      analysis += `Among ${collegeName} applicants, you're positioned as a ${tier} candidate with ${academicStrength} and ${activityQuality} extracurricular involvement.\n\n`;
      
      if (analysis.confidenceInterval) {
        analysis += `Confidence Range: ${analysis.confidenceInterval.lower}% - ${analysis.confidenceInterval.upper}%\n\n`;
        const range = analysis.confidenceInterval.upper - analysis.confidenceInterval.lower;
        if (range > 20) {
          analysis += `This wide range suggests several factors could significantly impact your chances - focus on the areas you can control! 🎯\n\n`;
        } else {
          analysis += `This narrow range indicates our prediction is quite reliable based on your current profile. 📊\n\n`;
        }
      }
      
      if (analysis.category) {
        const categoryLabels = {
          'reach': 'Reach School 🚀',
          'target': 'Target School 🎯', 
          'safety': 'Safety School ✅'
        };
        analysis += `Application Category: ${categoryLabels[analysis.category] || analysis.category}\n\n`;
      }
      
      return analysis;
    };
    
    // Generate strategic recommendations
    const generateRecommendations = () => {
      let analysis = `## 💡 Strategic Recommendations\n\n`;
      
      if (Array.isArray(analysis.recommendations) && analysis.recommendations.length > 0) {
        analysis += `### Immediate Actions\n`;
        analysis.recommendations.forEach((rec, index) => {
          analysis += `${index + 1}. ${rec} 📝\n`;
        });
      } else {
        // Generate dynamic recommendations based on profile gaps
        const recommendations = [];
        
        if (userStats.gpa && userStats.gpa < 3.5) {
          recommendations.push("Focus on maintaining or improving your GPA in remaining semesters");
        }
        
        if (userStats.sat && userStats.sat < 1300) {
          recommendations.push("Consider retaking the SAT with targeted preparation");
        }
        
        if (userStats.act && userStats.act < 28) {
          recommendations.push("Consider retaking the ACT with focused study");
        }
        
        if (!analysis.activityImpacts || analysis.activityImpacts.length === 0) {
          recommendations.push("Develop 2-3 high-impact extracurricular activities with leadership roles");
        }
        
        if (recommendations.length === 0) {
          recommendations.push("Focus on crafting compelling essays that showcase your unique story");
          recommendations.push("Secure strong letters of recommendation from teachers who know you well");
        }
        
        analysis += `### Immediate Actions\n`;
        recommendations.forEach((rec, index) => {
          analysis += `${index + 1}. ${rec} 📋\n`;
        });
      }
      
      analysis += `\n### Long-term Strategy\n`;
      analysis += `- Application Timing: Submit early if possible to demonstrate strong interest\n`;
      analysis += `- Demonstrated Interest: Visit campus, attend information sessions, and engage with admissions representatives\n`;
      analysis += `- Essay Development: Start early and craft authentic, compelling personal statements\n`;
      analysis += `- Recommendation Strategy: Build strong relationships with teachers who can write detailed, positive recommendations\n\n`;
      
      return analysis;
    };
    
    // Generate bottom line assessment
    const generateBottomLine = () => {
      const tier = getAdmissionTier(admissionChance);
      
      let analysis = `---\n## 🎓 Bottom Line\n\n`;
      
      const assessments = {
        high: `Excellent Position: Your ${admissionChance}% chance puts you in a strong position for admission. Focus on maintaining momentum and crafting compelling essays that showcase your personality and goals. Your hard work is paying off! 🎉`,
        competitive: `Competitive Standing: Your ${admissionChance}% chance shows you're competitive but not guaranteed. This is the sweet spot where strong essays, recommendations, and interviews can make all the difference. Keep pushing! 💪`,
        challenging: `Ambitious Reach: Your ${admissionChance}% chance makes this a challenging but achievable goal. Focus on what you can control: stellar essays, strong recommendations, and showcasing your unique story. Sometimes the most rewarding victories come from the biggest challenges! 🔥`,
        reach: `Dream School Challenge: Your ${admissionChance}% chance makes this a true reach school. Dream schools are called that for a reason - they're worth dreaming about! Focus on creating the strongest application possible. You never know what might happen! 🚀`
      };
      
      analysis += assessments[tier] || assessments.competitive;
      
      if (analysis.confidenceInterval) {
        analysis += `\n\nConfidence Range: ${analysis.confidenceInterval.lower}% - ${analysis.confidenceInterval.upper}%`;
      }
      
      analysis += `\n\nRemember: College admissions are unpredictable, and your worth isn't defined by any single acceptance or rejection. You're building an amazing future regardless of where you end up! ✨`;
      
      return analysis;
    };
    
    // Build the complete breakdown
    let breakdown = generateTitle();
    breakdown += `\n\n`;
    breakdown += generateAcademicSummary();
    breakdown += generateAcademicAnalysis();
    breakdown += generateActivityAnalysis();
    breakdown += generateCompetitivePosition();
    breakdown += generateRecommendations();
    breakdown += generateBottomLine();
    
    return breakdown;
  };

  const handleSaveProfile = (profile) => {
    setUserProfile(profile);
    localStorage.setItem('academicProfile', JSON.stringify(profile));
    // Don't close the panel on save - let user close it manually
  };

  // SVG Icons for breakdown buttons
  const GPASVG = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 1L9.5 5.5H14L10.25 8.5L11.75 13L8 10L4.25 13L5.75 8.5L2 5.5H6.5L8 1Z" fill="currentColor"/>
    </svg>
  );

  const SATSVG = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 2H14V4H2V2ZM2 6H14V8H2V6ZM2 10H14V12H2V10ZM2 14H14V16H2V14Z" fill="currentColor"/>
    </svg>
  );

  const ACTSVG = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" fill="none"/>
      <path d="M6 8L7.5 9.5L10.5 6.5" stroke="currentColor" strokeWidth="2" fill="none"/>
    </svg>
  );

  // Additional SVG icons for collapsible sections
  const BookSVG = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 2H14V14H2V2ZM4 4V12H12V4H4Z" fill="currentColor"/>
      <path d="M6 6H10V8H6V6ZM6 10H10V12H6V10Z" fill="currentColor"/>
    </svg>
  );

  const ChartSVG = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 14H14V2H2V14ZM4 12V6H6V12H4ZM8 12V4H10V12H8ZM12 12V8H14V12H12Z" fill="currentColor"/>
    </svg>
  );

  const SearchSVG = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="2" fill="none"/>
      <path d="M10 10L14 14" stroke="currentColor" strokeWidth="2" fill="none"/>
    </svg>
  );

  const LightbulbSVG = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 1C5.24 1 3 3.24 3 6C3 7.5 3.5 8.9 4.5 10L5 10.5V12C5 12.55 5.45 13 6 13H10C10.55 13 11 12.55 11 12V10.5L11.5 10C12.5 8.9 13 7.5 13 6C13 3.24 10.76 1 8 1ZM9 14H7V15H9V14Z" fill="currentColor"/>
    </svg>
  );

  // Get the appropriate SVG icon based on stat name
  const getStatIcon = (statName) => {
    switch (statName) {
      case 'GPA':
        return <GPASVG />;
      case 'SAT':
        return <SATSVG />;
      case 'ACT':
        return <ACTSVG />;
      default:
        return <GPASVG />;
    }
  };

  return (
    <div className={styles.container}>
      <motion.div 
        className={styles.headerImage} 
        style={{ backgroundImage: `url(${getHeaderImage(foundCollege.name)})` }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          {foundCollege.name}
        </motion.h1>
      </motion.div>
      
      <div className={styles.content}>
        <div className={styles.mainContent}>
          {/* Admission Chance Card */}
          <motion.div 
            className={styles.admissionChanceCard}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
          >
            <div className={styles.admissionChanceHeader}>
              <h2 className={styles.sectionTitle}>Admission Chance</h2>
              <button
                className={styles.breakdownButton}
                onClick={() => setChanceBreakdownModal({ open: true, content: getChanceBreakdown() })}
              >
                Chance Breakdown
              </button>
            </div>
            
            <div className={styles.admissionChanceContent}>
              {isLoading ? (
                <motion.div 
                  className={styles.loadingSpinner}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                />
              ) : error ? (
                <motion.div 
                  className={styles.errorState}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {error}
                </motion.div>
              ) : analysis ? (
                <div className={styles.circularGaugeContainer}>
                  <CircularProgressGauge
                    percentage={analysis.admissionChance}
                    label="Admission Chance"
                  />
                </div>
              ) : null}
              
              <div className={styles.chanceDisclaimer}>
                <p>This prediction is based on predictive analytics and historical admission data. Results are estimates and not guaranteed outcomes.</p>
              </div>
            </div>
          </motion.div>

          {/* How You Fit - Radar Chart */}
          <motion.div 
            className={styles.howYouFitSection}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
          >
            <h2 className={styles.sectionTitle}>How You Fit</h2>
            {radarMetrics.length > 0 ? (
              <MultiMetricRadarChart 
                metrics={radarMetrics}
                title="Multi-Dimensional Profile Analysis"
                className={styles.radarChartWrapper}
                showLegend={true}
                showTooltip={true}
              />
            ) : (
              <motion.div 
                className={styles.emptyRadarState}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <p>Complete your profile to see your comprehensive fit analysis!</p>
                <div className={styles.missingDataHints}>
                  <h4>We need:</h4>
                  <ul>
                    {!userProfile.gpa && <li>GPA</li>}
                    {!userProfile.sat && !userProfile.act && <li>SAT or ACT score</li>}
                    {(!JSON.parse(localStorage.getItem('collegeActivities')) || 
                      JSON.parse(localStorage.getItem('collegeActivities')).length === 0) && 
                      <li>Extracurricular activities</li>}
                  </ul>
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Statistics Comparison */}
          <motion.div 
            className={styles.statsSection}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
          >
            <div className={styles.sectionHeaderWithActions}>
              <div className={styles.sectionTitleWrapper}>
                <h2 className={styles.sectionTitle}>Academic Comparison</h2>
                <p className={styles.sectionSubtitle}>
                  See how your academic profile compares to admitted students at {foundCollege.name}
                </p>
              </div>
              <div className={styles.headerActions}>
                <button 
                  className={styles.editProfileButton}
                  onClick={() => setIsEditingProfile(true)}
                >
                  Edit Profile
                </button>
              </div>
            </div>
            {statData.length > 0 ? (
              <>
                <motion.div 
                  className={styles.statsList}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ staggerChildren: 0.1, delayChildren: 0.2 }}
                >
                  {statData.map((stat, index) => (
                    <motion.div
                      key={stat.name}
                      variants={{
                        hidden: { opacity: 0, y: 20 },
                        visible: { opacity: 1, y: 0 }
                      }}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: index * 0.1 }}
                    >
                      <EnhancedStatBar stat={stat} />
                    </motion.div>
                  ))}
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <StatLegend />
                </motion.div>
              </>
            ) : (
              <motion.div 
                className={styles.emptyStatsState}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className={styles.emptyStateIcon}>
                  {/* SVG Bar Chart Icon */}
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="8" y="28" width="6" height="12" rx="2" fill="#2196F3"/>
                    <rect x="20" y="18" width="6" height="22" rx="2" fill="#90CAF9"/>
                    <rect x="32" y="10" width="6" height="30" rx="2" fill="#1976D2"/>
                  </svg>
                </div>
                <h3>Complete Your Academic Profile</h3>
                <p>Add your test scores and GPA to see detailed comparisons with {foundCollege.name}'s admitted students.</p>
                <div className={styles.missingDataCard}>
                  <h4>We need:</h4>
                  <ul>
                    {!userProfile.gpa && <li>GPA (4.0 scale)</li>}
                    {!userProfile.sat && <li>SAT score</li>}
                    {!userProfile.act && <li>ACT score</li>}
                    {!userProfile.classRank && <li>Class Rank</li>}
                    {!userProfile.gender && <li>Gender</li>}
                    {!userProfile.ethnicity && <li>Ethnicity</li>}
                  </ul>
                </div>
                <button 
                  className={styles.addDataButton}
                  onClick={() => setIsEditingProfile(true)}
                >
                  + Add Academic Data
                </button>
              </motion.div>
            )}
          </motion.div>

          {/* Activity Impact Analysis */}
          <motion.div 
            className={styles.activityImpactSection}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
          >
            <h2 className={styles.sectionTitle}>Activity Impact Analysis</h2>
            <AnimatePresence>
              {isLoading ? (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  Analyzing activities...
                </motion.p>
              ) : error ? (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  Could not load activity analysis.
                </motion.p>
              ) : analysis && Array.isArray(analysis.activityImpacts) && analysis.activityImpacts.length > 0 ? (
                <motion.div 
                  className={styles.impactGrid}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ staggerChildren: 0.1 }}
                >
                  {analysis.activityImpacts.map((item, index) => (
                    <motion.div 
                      key={index} 
                      className={styles.impactCard}
                      variants={cardVariants}
                      initial="hidden"
                      animate="visible"
                      whileHover="hover"
                      layout
                    >
                      <div className={styles.impactHeader}>
                        <h4 className={styles.impactName}>{item.name}</h4>
                        <motion.div 
                          className={styles.impactScore}
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: index * 0.1 + 0.5, type: "spring" }}
                        >
                          <span>{item.impact}/10</span>
                        </motion.div>
                      </div>
                      <p className={styles.impactJustification}>{item.justification}</p>
                    </motion.div>
                  ))}
                </motion.div>
              ) : (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  No activities provided to analyze. Add some from the Compass page!
                </motion.p>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Application Deadlines Timeline */}
          <motion.div 
            className={styles.deadlineTimelineSection}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
          >
            <DeadlineTimeline 
              deadlines={deadlineData}
              title={`${foundCollege.name} Application Timeline`}
              className={styles.deadlineTimelineWrapper}
            />
          </motion.div>
        </div>

        {/* Sidebar */}
        <motion.div 
          className={styles.sidebar}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div 
            className={styles.quickFacts}
            variants={cardVariants}
          >
            <h3 className={styles.sidebarTitle}>Quick Facts</h3>
            <motion.ul 
              className={styles.factsList}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ staggerChildren: 0.1 }}
            >
              {[
                { label: 'Acceptance Rate', value: `${foundCollege.stats.acceptanceRate}%` },
                { label: 'Tuition', value: foundCollege.stats.cost?.tuitionAndFees || 'N/A' },
                { label: 'Total Students', value: foundCollege.stats.studentCount || 'N/A' },
                { label: 'Student-Faculty Ratio', value: foundCollege.stats.studentFacultyRatio || 'N/A' }
              ].map((fact, index) => (
                <motion.li 
                  key={fact.label}
                  variants={{
                    hidden: { opacity: 0, x: -10 },
                    visible: { opacity: 1, x: 0 }
                  }}
                >
                  <strong>{fact.label}:</strong> {fact.value}
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
        </motion.div>
      </div>

      {/* Modal for breakdown */}
      <AnimatePresence>
        {breakdownModal.open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={styles.modalOverlay}
            onClick={() => setBreakdownModal({ open: false, stat: null, content: '', suggestions: null })}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={styles.modalContainer}
              onClick={e => e.stopPropagation()}
            >
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>{breakdownModal.stat} Analysis</h3>
                <p className={styles.modalSubtitle}>Detailed breakdown and recommendations</p>
              </div>
              
              <div className={styles.modalBody}>
                {parseModalContent(breakdownModal.content)}
                
                {breakdownModal.suggestions && (
                  <div className={styles.modalSuggestions}>
                    <h4 className={styles.modalSuggestionsTitle}>
                      💡 Recommendations
                    </h4>
                    <ul className={styles.modalSuggestionsList}>
                      {breakdownModal.suggestions.map((suggestion, i) => (
                        <li key={i} className={styles.modalSuggestionItem}>
                          <div className={styles.modalSuggestionIcon}></div>
                          <span className={styles.modalSuggestionText}>{suggestion}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              
              <div className={styles.modalActions}>
                <button 
                  className={styles.modalCloseButton}
                  onClick={() => setBreakdownModal({ open: false, stat: null, content: '', suggestions: null })}
                >
                  Got it
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal for Chance Breakdown */}
      <AnimatePresence>
        {chanceBreakdownModal.open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={styles.modalOverlay}
            onClick={() => setChanceBreakdownModal({ open: false, content: '' })}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={styles.modalContainer}
              onClick={e => e.stopPropagation()}
            >
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>Admission Chance Analysis</h3>
                <p className={styles.modalSubtitle}>How we calculated your personalized chances</p>
              </div>
              
              <div className={styles.modalBody}>
                <div className={styles.modalContent} style={{ whiteSpace: 'pre-line' }}>
                  {parseModalContent(chanceBreakdownModal.content)}
                </div>
              </div>
              
              <div className={styles.modalActions}>
                <button 
                  className={styles.modalCloseButton}
                  onClick={() => setChanceBreakdownModal({ open: false, content: '' })}
                >
                  Got it
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Profile Panel */}
      <ProfilePanel
        isOpen={isEditingProfile}
        onClose={() => setIsEditingProfile(false)}
        onSave={handleSaveProfile}
        initialProfile={userProfile}
      />
    </div>
  );
};

export default CollegeAnalytics; 